/**
 * B24Frame Context
 *
 * Provides Bitrix24 B24Frame SDK instance to embedded routes.
 * Initialized once in /embedded layout and shared across all embedded routes.
 *
 * Architecture (per AI Council recommendation):
 * - B24Frame initialized once at layout level
 * - Shared via React Context to prevent SDK destruction on navigation
 * - Child components receive via useB24Frame() hook
 */

import { createContext, useContext } from 'react';
import type { B24Frame } from '@bitrix24/b24jssdk';

export interface Bitrix24User {
  email: string;
  name: string;
  memberId: string;
  domain: string;
  userId: string; // CRM User ID (e.g. "42" from Bitrix24)
  language?: string; // LANGUAGE_ID from Bitrix24 (e.g. "ru", "en", "uk")
}

export interface B24FrameContextValue {
  b24Frame: B24Frame | null;
  bitrix24User: Bitrix24User | null;
  isAuthenticated: boolean;
}

const B24FrameContext = createContext<B24FrameContextValue | null>(null);

export function useB24Frame(): B24FrameContextValue {
  const context = useContext(B24FrameContext);
  if (!context) {
    throw new Error('useB24Frame must be used within B24FrameProvider');
  }
  return context;
}

export const B24FrameProvider = B24FrameContext.Provider;
